import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Target, Bookmark, FlaskConical, Menu } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { mobileColors } from '../../theme/mobileTokens';
import MoreMenuSheet from './MoreMenuSheet';

/**
 * MobileBottomNav — fixed 64px bottom tab bar visible on every page (mobile only).
 *
 *   [ Screener ] [ Watchlist ] [ Research ] [ More ]
 *
 * Tabs reflect the current top-level route. "More" opens a sheet rather than
 * navigating; it lists routes that don't fit in the four-tab nav (Profile,
 * Stock Analyzer, Dashboard Builder, Help, Subscription, Sign out).
 */
export default function MobileBottomNav() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!isMobile) return null;

  // Don't render on auth/marketing routes — only on app-shell routes
  const hideOnPaths = ['/login', '/signup', '/request-password-reset', '/terms', '/privacy', '/pricing'];
  if (hideOnPaths.some(p => location.pathname.startsWith(p)) || location.pathname.startsWith('/reset-password')) {
    return null;
  }

  const tabs = [
    { key: 'screener',  label: 'Screener',  icon: Target,        path: '/' },
    { key: 'watchlist', label: 'Watchlist', icon: Bookmark,      path: '/watchlist' },
    { key: 'research',  label: 'Research',  icon: FlaskConical,  path: '/research' },
    { key: 'more',      label: 'More',      icon: Menu,          isMore: true },
  ];

  const isActive = (tab) => {
    if (tab.isMore) return moreOpen;
    if (tab.path === '/') return location.pathname === '/' || location.pathname === '';
    return location.pathname.startsWith(tab.path);
  };

  const handleTap = (tab) => {
    if (tab.isMore) {
      setMoreOpen(true);
      return;
    }
    navigate(tab.path);
  };

  return (
    <>
      <nav
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 64,
          background: mobileColors.navBg,
          borderTop: `0.5px solid ${mobileColors.borderSubtle}`,
          display: 'flex',
          padding: '4px 0 8px',
          paddingBottom: 'max(8px, env(safe-area-inset-bottom, 8px))',
          zIndex: 1000,
        }}
        aria-label="Primary navigation"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(tab);
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTap(tab)}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
                background: 'transparent',
                border: 'none',
                color: active ? mobileColors.accentBlue : mobileColors.textTertiary,
                cursor: 'pointer',
                padding: '4px 6px',
                transition: 'transform 0.08s, color 0.12s',
              }}
              onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.96)'; }}
              onTouchEnd={(e) => { e.currentTarget.style.transform = ''; }}
              aria-current={active ? 'page' : undefined}
            >
              <Icon size={21} />
              <span style={{ fontSize: 11, fontWeight: 500 }}>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      <MoreMenuSheet isOpen={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
